import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { PortalDsAlert } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { SelfServiceCampaignDetail } from '../types/self-service-campaign.types';
import { formatLaunchedAt } from '../utils/campaigns.utils';
import { CampaignCreativeCard } from './campaign-creative-card';
import {
  formatSelfServiceAudience,
  formatSelfServiceCurrency,
  formatSelfServiceDate,
  formatSelfServiceStatus,
} from '../utils/self-service-campaign.utils';

function DetailField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <dt className="text-[12px] font-medium uppercase tracking-[0.04em] text-[#9CA3AF]">
        {label}
      </dt>
      <dd className="text-[14px] text-[#0D0F1A]">
        {children}
      </dd>
    </div>
  );
}

interface SelfServiceCampaignDetailSectionProps {
  detail: SelfServiceCampaignDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  isSyncNotConfigured: boolean;
  hasExternalId: boolean;
}

export function SelfServiceCampaignDetailSection({
  detail,
  isLoading,
  isError,
  isSyncNotConfigured,
  hasExternalId,
}: SelfServiceCampaignDetailSectionProps) {
  if (!hasExternalId) {
    return (
      <PortalDsAlert
        variant="warning"
        title="No self-service campaign linked"
      >
        This request was ingested without an external campaign id, so live details cannot be loaded.
      </PortalDsAlert>
    );
  }

  if (isSyncNotConfigured) {
    return (
      <PortalDsAlert
        variant="warning"
        title="Self-service connection unavailable"
      >
        <code className="rounded bg-[#F0F1F5] px-1 py-0.5 text-[12px]">
          PORTAL_SYNC_SECRET
        </code>
        {' '}
        and
        {' '}
        <code className="rounded bg-[#F0F1F5] px-1 py-0.5 text-[12px]">
          SELF_SERVICE_BASE_URL
        </code>
        {' '}
        must be configured on the portal backend.
      </PortalDsAlert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-[#5E6278]">
        <Loader2
          className="size-5 animate-spin"
          aria-hidden
        />
        <span>
          Loading self-service campaign…
        </span>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <PortalDsAlert
        variant="danger"
        title="Could not load self-service campaign"
      >
        The campaign may have been removed in self-service, or the service is temporarily unavailable.
      </PortalDsAlert>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#5E6278]">
          Campaign (self-service)
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField label="Name">
            {detail.name}
          </DetailField>
          <DetailField label="Status">
            {formatSelfServiceStatus(detail.status)}
          </DetailField>
          <DetailField label="State">
            {formatSelfServiceStatus(detail.state)}
          </DetailField>
          <DetailField label="Budget">
            {formatSelfServiceCurrency(detail.budget)}
          </DetailField>
          <DetailField label="Spend">
            {formatSelfServiceCurrency(detail.spend)}
          </DetailField>
          <DetailField label="Start date">
            {formatSelfServiceDate(detail.start_date)}
          </DetailField>
          <DetailField label="End date">
            {formatSelfServiceDate(detail.end_date)}
          </DetailField>
          <DetailField label="Audience">
            {formatSelfServiceAudience(detail.audience)}
          </DetailField>
          <DetailField label="Geo target">
            {detail.geo_target || '—'}
          </DetailField>
          <DetailField label="Landing URL">
            {detail.landing_url ? (
              <a
                href={detail.landing_url}
                target="_blank"
                rel="noreferrer"
                className="break-all text-[#2563EB] hover:underline"
              >
                {detail.landing_url}
              </a>
            ) : '—'}
          </DetailField>
          <DetailField label="Created by">
            {detail.created_by
              ? `${detail.created_by.name} (${detail.created_by.email})`
              : '—'}
          </DetailField>
          <DetailField label="Workspace">
            {detail.workspace?.name ?? '—'}
          </DetailField>
          <DetailField label="Brand">
            {detail.brand ? (
              <span className="inline-flex items-center gap-2">
                {detail.brand.color ? (
                  <span
                    className="inline-block size-3 rounded-full border border-[#EDEEF2]"
                    style={{ backgroundColor: detail.brand.color }}
                    aria-hidden
                  />
                ) : null}
                {detail.brand.name}
              </span>
            ) : '—'}
          </DetailField>
        </dl>
        {detail.notes ? (
          <div className="mt-4">
            <DetailField label="Notes">
              {detail.notes}
            </DetailField>
          </div>
        ) : null}
      </section>

      {detail.creatives.length > 0 ? (
        <section>
          <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#5E6278]">
            Creatives
          </h2>
          <ul className="space-y-4">
            {detail.creatives.map((creative) => (
              <CampaignCreativeCard
                key={creative.id}
                creative={creative}
              />
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#5E6278]">
          Self-service metadata
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField label="Campaign ID">
            <span className="font-mono text-[13px] break-all">
              {detail.id}
            </span>
          </DetailField>
          <DetailField label="Organisation ID">
            <span className="font-mono text-[13px] break-all">
              {detail.organization_id}
            </span>
          </DetailField>
          <DetailField label="Created at">
            <span className="font-mono text-[13px]">
              {formatLaunchedAt(detail.created_at)}
            </span>
          </DetailField>
          <DetailField label="Updated at">
            <span className="font-mono text-[13px]">
              {formatLaunchedAt(detail.updated_at)}
            </span>
          </DetailField>
        </dl>
      </section>
    </div>
  );
}
